import type { ColumnDef } from "@tanstack/react-table";
import { Info, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { MailOutboxItem } from "@/lib/notification.api";
import { formatMailDateTime, humanizeTemplateKey } from "./mailOutboxPage.helpers";
import { MailOutboxSourceLink } from "./components/MailOutboxSourceLink";
import { MailOutboxStatusBadge } from "./components/MailOutboxStatusBadge";

interface CreateMailOutboxColumnsOptions {
  retryingId: number | null;
  onOpenDetail: (item: MailOutboxItem) => void;
  onRetry: (id: number) => void;
}

export function createMailOutboxColumns({
  retryingId,
  onOpenDetail,
  onRetry,
}: CreateMailOutboxColumnsOptions): ColumnDef<MailOutboxItem, any>[] {
  return [
    {
      accessorKey: "createdAt",
      header: "Date / heure",
      cell: ({ row }) => (
        <span className="text-[12px] text-body">
          {formatMailDateTime(row.original.createdAt)}
        </span>
      ),
    },
    {
      accessorKey: "recipient",
      header: "Destinataire",
      cell: ({ row }) => <span className="text-[12px] text-body">{row.original.recipient}</span>,
    },
    {
      accessorKey: "subject",
      header: "Sujet",
      cell: ({ row }) => (
        <span className="max-w-[280px] truncate text-[12px] text-body" title={row.original.subject}>
          {row.original.subject}
        </span>
      ),
    },
    {
      accessorKey: "templateKey",
      header: "Modèle",
      cell: ({ row }) => (
        <span className="text-[12px] text-muted-foreground">
          {humanizeTemplateKey(row.original.templateKey)}
        </span>
      ),
    },
    {
      accessorKey: "status",
      header: "Statut",
      cell: ({ row }) => <MailOutboxStatusBadge status={row.original.status} />,
    },
    {
      id: "source",
      header: "Source",
      cell: ({ row }) => <MailOutboxSourceLink item={row.original} />,
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <div className="flex items-center justify-end gap-1.5">
          <button
            type="button"
            onClick={() => onOpenDetail(row.original)}
            className="text-subtle hover:text-body"
            title="Détails"
          >
            <Info size={14} />
          </button>
          {row.original.status === "failed" && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2 text-[11px]"
              disabled={retryingId === row.original.id}
              onClick={() => onRetry(row.original.id)}
              title="Renvoyer cet email"
            >
              {retryingId === row.original.id ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <RefreshCw size={12} />
              )}
              Renvoyer
            </Button>
          )}
        </div>
      ),
    },
  ];
}
